import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Auth, signInWithCustomToken, signOut, onAuthStateChanged } from '@angular/fire/auth';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, firstValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { User as AppUser, UserRole, UserStatus, VALID_ROLES } from '../models/user.model';
import { TenantService } from '../services/tenant.service';
import { environment } from 'src/environments/environment';

// Error codes for login failures
export enum AuthErrorCode {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  USER_INACTIVE = 'USER_INACTIVE',
  TENANT_MISMATCH = 'TENANT_MISMATCH',
  INVALID_ROLE = 'INVALID_ROLE',
  TEACHER_NO_CLASSES = 'TEACHER_NO_CLASSES',
  PARENT_NO_CHILDREN = 'PARENT_NO_CHILDREN',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface AuthError {
  code: AuthErrorCode;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private tenantService = inject(TenantService);
  private envInjector = inject(EnvironmentInjector);
  private http = inject(HttpClient);
  private currentUserSubject = new BehaviorSubject<AppUser | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private authErrorSubject = new BehaviorSubject<AuthError | null>(null);
  public authError$ = this.authErrorSubject.asObservable();

  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  public authInitialized$ = this.authInitializedSubject.asObservable();

  private getCollectionName(): string {
    return this.tenantService.getCollectionName('users');
  }

  private getbaseAppUrl(): string {
    return environment.baseApiUrl;
  }

  constructor() {
    // Monitor auth state changes - this is the single source of truth for user state
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      // Clear any previous auth errors when auth state changes
      this.authErrorSubject.next(null);

      try {
        if (firebaseUser) {
          console.log('[AuthService] onAuthStateChanged: signed in', {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
          });

          // Lookup user by UID in tenant's users collection
          const collectionPath = this.getCollectionName();
          const tenantId = this.tenantService.getTenantId();

          console.log('[AuthService] looking up user by UID', {
            uid: firebaseUser.uid,
            collectionPath,
            tenantId,
          });

          const userDoc = await runInInjectionContext(this.envInjector, () =>
            getDoc(doc(this.firestore, collectionPath, firebaseUser.uid))
          );

          if (!userDoc.exists()) {
            // User not found in tenant's users collection
            console.warn('[AuthService] User not found in tenant collection', {
              uid: firebaseUser.uid,
              tenantId,
            });

            // Sign out the user and emit error
            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.USER_NOT_FOUND,
              message: 'User not found. Please contact your administrator.'
            });
            return;
          }

          const userData = userDoc.data();
          const userTenantId = userData['tenantId'] as string | undefined;
          const userStatus = (userData['status'] as UserStatus) || 'active'; // Default to active for backward compatibility
          const userRole = userData['role'] as UserRole;

          console.log('[AuthService] user found in Firestore', {
            uid: firebaseUser.uid,
            role: userRole,
            status: userStatus,
            tenantId: userTenantId,
          });

          // Validate tenant ID matches
          if (userTenantId && tenantId && userTenantId !== tenantId) {
            console.warn('[AuthService] Tenant mismatch', {
              userTenantId,
              currentTenantId: tenantId,
            });

            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.TENANT_MISMATCH,
              message: 'User does not belong to this organization.'
            });
            return;
          }

          // Check if user is active
          if (userStatus !== 'active') {
            console.warn('[AuthService] User is not active', {
              uid: firebaseUser.uid,
              status: userStatus,
            });

            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.USER_INACTIVE,
              message: 'Your account is inactive. Please contact your administrator.'
            });
            return;
          }

          // Validate role is a known valid role
          if (!userRole || !VALID_ROLES.includes(userRole)) {
            console.warn('[AuthService] Invalid role', {
              uid: firebaseUser.uid,
              role: userRole,
            });

            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.INVALID_ROLE,
              message: 'Your account has an invalid role. Please contact your administrator.'
            });
            return;
          }

          // Role-specific validation: teachers must have classes assigned
          const assignedClasses = userData['assignedClasses'] as string[] | undefined;
          if (userRole === 'teacher' && (!assignedClasses || assignedClasses.length === 0)) {
            console.warn('[AuthService] Teacher has no classes assigned', {
              uid: firebaseUser.uid,
            });

            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.TEACHER_NO_CLASSES,
              message: 'Your account has no classes assigned yet. Please contact your administrator.'
            });
            return;
          }

          // Role-specific validation: parents must have children linked
          const children = userData['children'] as string[] | undefined;
          if (userRole === 'parent' && (!children || children.length === 0)) {
            console.warn('[AuthService] Parent has no children linked', {
              uid: firebaseUser.uid,
            });

            await signOut(this.auth);
            this.currentUserSubject.next(null);
            this.authErrorSubject.next({
              code: AuthErrorCode.PARENT_NO_CHILDREN,
              message: 'No students are linked to your account yet. Please contact your administrator.'
            });
            return;
          }

          // Build and emit the user object
          const appUser: AppUser = {
            id: firebaseUser.uid,
            email: firebaseUser.email || userData['email'] || '',
            name: userData['name'] || '',
            role: userRole || 'parent',
            status: userStatus,
            tenantId: userTenantId || tenantId,
            phoneNumber: userData['phoneNumber'] || '',
            profilePicture: userData['profilePicture'] || '',
            grade: userData['grade'],
            assignedClasses: userData['assignedClasses'],
            subject: userData['subject'],
            subjects: userData['subjects'],
            incharge: userData['incharge'],
            children: userData['children'],
            createdAt: userData['createdAt'],
            updatedAt: userData['updatedAt']
          };

          console.log('[AuthService] emitting appUser', appUser);
          this.currentUserSubject.next(appUser);

        } else {
          // User signed out
          console.log('[AuthService] onAuthStateChanged: signed out');
          this.currentUserSubject.next(null);
        }
      } catch (error) {
        console.error('[AuthService] Error in onAuthStateChanged:', error);
        this.currentUserSubject.next(null);
        this.authErrorSubject.next({
          code: AuthErrorCode.UNKNOWN_ERROR,
          message: 'An unexpected error occurred. Please try again.'
        });
      } finally {
        this.authInitializedSubject.next(true);
      }
    });
  }

  /**
   * Request OTP for email
   */
  async requestOTP(email: string): Promise<void> {
    const tenantId = this.tenantService.getTenantId();
    const baseApiUrl = `${this.getbaseAppUrl()}/api/auth/otp/request`;

    console.log('[AuthService] requestOTP()', {
      email: email.toLowerCase(),
      tenantId,
      baseApiUrl,
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const body = { email, tenantId };

    await firstValueFrom(
      this.http.post<void>(baseApiUrl, body, { headers }).pipe(
        catchError((error: any) => {
          const errorData = error.error || {};
          return throwError(() => new Error(errorData.error || 'Failed to request OTP'));
        })
      )
    );
  }

  /**
   * Verify OTP and sign in with custom token
   * This method only triggers signInWithCustomToken.
   * The onAuthStateChanged handler will handle user lookup and validation.
   */
  async verifyOTP(email: string, otp: string): Promise<void> {
    const tenantId = this.tenantService.getTenantId();
    const baseApiUrl = `${this.getbaseAppUrl()}/api/auth/otp/verify`;

    console.log('[AuthService] verifyOTP()', {
      email: email.toLowerCase(),
      tenantId,
      baseApiUrl,
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const body = { email, otp, tenantId };

    const data = await firstValueFrom(
      this.http.post<{ token: string }>(baseApiUrl, body, { headers }).pipe(
        catchError((error: any) => {
          const errorData = error.error || {};
          return throwError(() => new Error(errorData.error || 'Invalid OTP'));
        })
      )
    );

    const customToken = data.token;

    // Sign in with custom token - onAuthStateChanged will handle the rest
    console.log('[AuthService] verifyOTP: triggering signInWithCustomToken');
    await signInWithCustomToken(this.auth, customToken);
    console.log('[AuthService] verifyOTP: signInWithCustomToken completed');
  }

  /**
   * Clear any auth errors
   */
  clearError(): void {
    this.authErrorSubject.next(null);
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    this.currentUserSubject.next(null);
    this.authErrorSubject.next(null);
  }

  getCurrentUser(): AppUser | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): Observable<boolean> {
    return this.currentUser$.pipe(map(user => !!user));
  }

  hasRole(role: UserRole): Observable<boolean> {
    return this.currentUser$.pipe(map(user => user?.role === role));
  }

  isOwner(): Observable<boolean> {
    return this.hasRole('owner');
  }

  isAdmin(): Observable<boolean> {
    return this.currentUser$.pipe(map(user => user?.role === 'owner' || user?.role === 'admin'));
  }

  isManager(): Observable<boolean> {
    return this.hasRole('manager');
  }

  /** Returns true for owner, admin, or manager */
  isManagement(): Observable<boolean> {
    return this.currentUser$.pipe(map(user =>
      user?.role === 'owner' || user?.role === 'admin' || user?.role === 'manager'
    ));
  }

  isTeacher(): Observable<boolean> {
    return this.hasRole('teacher');
  }

  isParent(): Observable<boolean> {
    return this.hasRole('parent');
  }
}
